import { Filter, Filters } from '../overview'
import { FilterOptionItem } from '../overview/filters/FilterOption'
import {
  Badge,
  BadgeList,
  Rank,
  RankList,
  Role,
  RoleList,
  CustomProfileField,
  CustomProfileFieldList,
  CustomProfileFieldOptions,
  Group
} from './Columns'
import DateFilterSelector, {
  DateFilterFilterSelectorItem
} from '../overview/filters/DateFilterSelector'
import moment, { unitOfTime } from 'moment'
import {
  createSeparator,
  createTextSelector
} from '../overview/filters/utils'
import AdvancedRadioInputFilterSelector from '../overview/filters/AdvancedRadioInputFilterSelector'
import CheckboxFilterSelector, {
  CheckboxFilterSelectorCondition
} from '../overview/filters/CheckboxFilterSelector'
import AdvancedTextInputFilter, { AdvancedTextInputFilterCondition } from '../overview/filters/AdvancedTextInputFilter'
import {
  FilterList,
  FilterSelector,
  TextSelector
} from '@insided/deprecated-control/overview/filters/FiltersMenu'

export type FilterValue =
  | string
  | number
  | string[]
  | number[]
  | { [key: string]: string | number | string[] | number[] }

export type FilterValueList = Record<string, FilterValue>

interface UserFilter {
  name: string
  value: FilterValue
}

export const dateSelectorItems: DateFilterFilterSelectorItem[] = [
  {
    id: 1,
    type: 'relative',
    name: 'Less than',
    value: 'lt',
    unit: 'day'
  },
  {
    id: 2,
    type: 'relative',
    name: 'More than',
    value: 'gt',
    unit: 'day'
  },
  {
    id: 3,
    type: 'absolute',
    name: 'Before',
    value: 'to'
  },
  {
    id: 4,
    type: 'absolute',
    name: 'On',
    value: 'eq'
  },
  {
    id: 5,
    type: 'absolute',
    name: 'After',
    value: 'from'
  },
  {
    id: 6,
    type: 'range',
    name: 'Between',
    value: 'range'
  }
]

const dateSelector = (id: string, name: string): FilterSelector =>
  DateFilterSelector.create({
    id: id,
    name: name,
    items: dateSelectorItems
  })

export const prepareGroups = (groupList: Group[]): FilterOptionItem[] => {
  return groupList.map(
    (group: Group): FilterOptionItem => ({
      id: group.id,
      name: group.title,
      value: group.id
    })
  )
}

export const prepareBadges = (badges: Badge[]): FilterOptionItem[] => {
  return badges.map(
    (badge: Badge): FilterOptionItem => ({
      id: badge.id,
      name: badge.title,
      value: badge.id
    })
  )
}

export const prepareRanks = (ranks: Rank[]): FilterOptionItem[] => {
  return ranks.map(
    (rank: Rank): FilterOptionItem => ({
      id: rank.id,
      name: rank.value,
      value: rank.id
    })
  )
}

const prepareProfileFieldOptions = (options?: CustomProfileFieldOptions): FilterOptionItem[] => {
  if (!options) {
    return []
  }

  const items: FilterOptionItem[] = []
  Object.keys(options).forEach((key: string) => {
    items.push({
      id: key,
      value: key,
      name: options[key]
    })
  })

  return items
}

export const normalizeUserRoles = (roles: Role[]): FilterOptionItem[] => {
  return roles.map(
    (role: Role): FilterOptionItem => ({
      id: role.id,
      name: role.label,
      value: role.id
    })
  )
}

const primaryRole = (roles: Role[]): FilterSelector =>
  CheckboxFilterSelector.create({
    id: 'primaryRole',
    name: 'Primary Role',
    items: normalizeUserRoles(roles),
    conditions: getCheckboxSelectorConditions(false)
  })

const ranks = (rankList: RankList): FilterSelector =>
  CheckboxFilterSelector.create({
    id: 'rank',
    name: 'Rank',
    items: prepareRanks(rankList),
    conditions: getCheckboxSelectorConditions()
  })

const customRoles = (roles: Role[]): FilterSelector =>
  CheckboxFilterSelector.create({
    id: 'customRoles',
    name: 'Custom role',
    items: normalizeUserRoles(roles),
    conditions: getCheckboxSelectorConditions()
  })

const groups = (groupList: Group[]): FilterSelector =>
  CheckboxFilterSelector.create({
    id: 'group',
    name: 'Groups',
    items: prepareGroups(groupList),
    conditions: getCheckboxSelectorConditions()
  })

const badges = (badgeList: BadgeList): FilterSelector =>
  CheckboxFilterSelector.create({
    id: 'badge',
    name: 'Badges',
    items: prepareBadges(badgeList),
    conditions: getCheckboxSelectorConditions()
  })

const sectionHeader = (id: string, title: string): TextSelector =>
  createTextSelector(id, <span>{title}</span>)

const joinDate = dateSelector('joinDate', 'Registration date')

const lastActivityAt = dateSelector('lastActivity', 'Last activity')

const lastLoginAt = dateSelector('lastLogin', 'Last login')

const numberFilterOperators = [
  {
    id: 'eq',
    name: 'Equal to',
    value: 'eq',
    type: 'relative'
  },
  {
    id: 'not_eq',
    name: 'Does not equal',
    value: 'not_eq',
    type: 'relative'
  },
  {
    id: 'lt',
    name: 'Less than',
    value: 'lt',
    type: 'relative'
  },
  {
    id: 'gt',
    name: 'More than',
    value: 'gt',
    type: 'relative'
  },
  {
    id: 'between',
    name: 'Between',
    value: 'between',
    type: 'range'
  }
]

const topics = (): FilterSelector =>
  AdvancedRadioInputFilterSelector.create({
    id: 'topics',
    name: 'Topics',
    items: numberFilterOperators
  })

export const replies = (): FilterSelector =>
  AdvancedRadioInputFilterSelector.create({
    id: 'replies',
    name: 'Replies',
    items: numberFilterOperators
  })

// const points = (): FilterSelector => AdvancedRadioInputFilterSelector.create({
//   id: 'points',
//   name: 'Points',
//   items: numberFilterOperators
// })

const advancedTextInputConditions: AdvancedTextInputFilterCondition[] = [
  { id: 'selector-contains', value: 'like', name: 'Contains', showInput: true },
  { id: 'selector-not-contains', value: 'not_like', name: 'Does not contain', showInput: true },
  { id: 'selector-equals', value: 'eq', name: 'Equals', showInput: true },
  { id: 'selector-not-equals', value: 'not_eq', name: 'Does not equal', showInput: true },
  { id: 'selector-starts-with', value: 'start', name: 'Starts with', showInput: true },
  { id: 'selector-ends-with', value: 'end', name: 'Ends with', showInput: true },
  { id: 'selector-empty', value: 'empty', name: 'Empty', showInput: false },
  { id: 'selector-not-empty', value: 'not_empty', name: 'Not empty', showInput: false }
]

const email = (): FilterSelector => AdvancedTextInputFilter.create({
  id: 'email',
  name: 'Email',
  conditions: advancedTextInputConditions
})

const getCheckboxSelectorConditions = (
  isEmptyAllowed: boolean = true
): CheckboxFilterSelectorCondition[] => {
  const conditions = [
    { id: 'selector-in', name: 'Is one of', value: 'in', showItems: true },
    { id: 'selector-not-in', name: 'Is not one of', value: 'not_in', showItems: true }
  ]

  if (isEmptyAllowed) {
    conditions.push(
      { id: 'selector-empty', name: 'Is empty', value: 'empty', showItems: false },
      { id: 'selector-not-empty', name: 'Is not empty', value: 'not_empty', showItems: false }
    )
  }

  return conditions
}

export const getDateParameters =
  (paramName: string) =>
    (rawFilter: {
      condition: string
      data: string | object
      type?: string
      unit?: unitOfTime.DurationConstructor
    }): UserFilter => {
      if (rawFilter.type === 'relative') {
        const duration = moment
            .duration(parseInt(rawFilter.data as string), 'd')
            .toISOString()

        const operator = rawFilter.condition === 'gt' ? 'to' : 'from'

        return {
          name: paramName,
          value: {
            [operator]: duration
          }
        }
      }

      if (rawFilter.type === 'range') {
        const { from, to } = rawFilter.data as { from: string, to: string }

        return {
          name: paramName,
          value: {
            from,
            to
          }
        }
      }

      if (rawFilter.condition === 'eq') {
        const date = moment.utc(rawFilter.data as string)

        const from = date.toISOString(false)
        const to = date.add('1', 'day').subtract('1', 'second').toISOString(false)

        return {
          name: paramName,
          value: {
            from,
            to
          }
        }
      }

      return {
        name: paramName,
        value: {
          [rawFilter.condition]: rawFilter.data as string
        }
      }
    }

const isConditionEmptyNotEmpty = (condition: string) =>
  ['empty', 'not_empty'].includes(condition)

const getTextParamValueFromRawFilter = ({ condition, data }: {
  condition: string
  data: string
}): FilterValue => ({
  [condition]: isConditionEmptyNotEmpty(condition) ? 1 : data
})

const getListParamValueFromRawFilter = ({ condition, data }: {
  condition: string
  data: Array<{ id: number }>
}): FilterValue => ({
  [condition]: isConditionEmptyNotEmpty(condition) ? 1 : data.map((item) => item.id)
})

const getNumberParamValueFromRawFilter = ({ condition, data }: {
  condition: string
  data: any
}): FilterValue => {
  if (condition === 'between') {
    const [firstRange, secondRange] = data
    return {
      gte: firstRange,
      lte: secondRange
    }
  }

  return { [condition]: data }
}

const filterParamGenerator: { [key: string]: (rawFilter: Filter) => UserFilter } = {
  email: (rawFilter): UserFilter => ({
    name: 'email',
    value: getTextParamValueFromRawFilter(rawFilter)
  }),
  replies: (rawFilter: { condition: string, data: number }): UserFilter => ({
    name: 'replies',
    value: getNumberParamValueFromRawFilter(rawFilter)
  }),
  topics: (rawFilter: { condition: string, data: number }): UserFilter => ({
    name: 'topics',
    value: getNumberParamValueFromRawFilter(rawFilter)
  }),
  points: (rawFilter: { condition: string, data: number }): UserFilter => ({
    name: 'points',
    value: getNumberParamValueFromRawFilter(rawFilter)
  }),
  primaryRole: (rawFilter: { condition: string, data: Array<{ id: number }> }): UserFilter => ({
    name: 'role',
    value: getListParamValueFromRawFilter(rawFilter)
  }),
  rank: (rawFilter: { condition: string, data: Array<{ id: number }> }): UserFilter => ({
    name: 'rank',
    value: getListParamValueFromRawFilter(rawFilter)
  }),
  customRoles: (rawFilter: { condition: string, data: Array<{ id: number }> }): UserFilter => ({
    name: 'custom_roles',
    value: getListParamValueFromRawFilter(rawFilter)
  }),
  badge: (rawFilter: { condition: string, data: Array<{ id: number }> }): UserFilter => ({
    name: 'badges',
    value: getListParamValueFromRawFilter(rawFilter)
  }),
  group: (rawFilter: { condition: string, data: Array<{ id: number }> }): UserFilter => ({
    name: 'groups',
    value: getListParamValueFromRawFilter(rawFilter)
  }),
  search: (rawFilter: string): UserFilter => ({
    name: 'search',
    value: {
      like: rawFilter
    }
  }),
  joinDate: getDateParameters('join_date'),
  lastActivity: getDateParameters('last_activity'),
  lastLogin: getDateParameters('last_visit')
}

const customProfileFieldFilterParamGenerator: {
  [key: string]: (rawFilter: Filter) => FilterValue
} = {
  birthday: (rawFilter) => getDateParameters('profile_field')(rawFilter).value,
  date: (rawFilter) => getDateParameters('profile_field')(rawFilter).value,
  text: (rawFilter) => getTextParamValueFromRawFilter(rawFilter),
  check: getListParamValueFromRawFilter,
  radio: getListParamValueFromRawFilter,
  select: getListParamValueFromRawFilter
}

export const getFilterParams = (
  selectedFilters: Filters,
  customProfileFields: CustomProfileFieldList
): FilterValueList => {
  const filters: { [key: string]: FilterValue } = {}
  Object.keys(selectedFilters).forEach((key: string) => {
    const rawFilter: Filter = selectedFilters[key]
    if (!rawFilter || !Object.keys(rawFilter).length) {
      return
    }

    if (key in filterParamGenerator) {
      const filter = filterParamGenerator[key](rawFilter)
      filters[filter.name] = filter.value

      return
    }

    const profileFieldRegex = /^profileField_(\d+)$/
    const result = key.match(profileFieldRegex)
    if (!result) {
      return
    }

    const [_, id] = result
    const customProfileField = customProfileFields.find((item) => String(item.id) === String(id))
    if (
      !customProfileField ||
      !(customProfileField.type in customProfileFieldFilterParamGenerator)
    ) {
      return
    }

    filters[`profile_field_${id}`] =
      customProfileFieldFilterParamGenerator[customProfileField.type](rawFilter)
  })

  return filters
}

const customProfileFieldCheckboxSelector = (
  customProfileField: CustomProfileField
): FilterSelector =>
  CheckboxFilterSelector.create({
    id: `profileField_${customProfileField.id}`,
    name: customProfileField.title,
    items: prepareProfileFieldOptions(customProfileField.data),
    conditions: getCheckboxSelectorConditions()
  })

const customProfileFieldGenerators: {
  [key: string]: (customProfileField: CustomProfileField) => FilterSelector
} = {
  birthday: (customProfileField: CustomProfileField) =>
    dateSelector(`profileField_${customProfileField.id}`, customProfileField.title),
  date: (customProfileField: CustomProfileField) =>
    dateSelector(`profileField_${customProfileField.id}`, customProfileField.title),
  text: (customProfileField: CustomProfileField) =>
    AdvancedTextInputFilter.create({
      id: `profileField_${customProfileField.id}`,
      name: customProfileField.title,
      conditions: advancedTextInputConditions
    }),
  check: customProfileFieldCheckboxSelector,
  radio: customProfileFieldCheckboxSelector,
  select: customProfileFieldCheckboxSelector
}

const generateCustomProfileFieldFilters = (
  customProfileFields: CustomProfileFieldList
): FilterList => {
  const profileFieldFilters: FilterList = []
  customProfileFields.forEach((customProfileField: CustomProfileField) => {
    if (
      !Object.keys(customProfileFieldGenerators).length ||
      !(customProfileField.type in customProfileFieldGenerators)
    ) {
      return
    }

    const filter = customProfileFieldGenerators[customProfileField.type](customProfileField)
    profileFieldFilters.push(filter)
  })

  return profileFieldFilters.length
    ? [
      createSeparator(),
      sectionHeader('customProfileFields', 'USER PROFILE FIELDS'),
      ...profileFieldFilters
    ]
    : []
}

export const getFilters = (
  badgeList: BadgeList,
  rankList: RankList,
  roleList: RoleList,
  groupList: Group[],
  customProfileFields: CustomProfileFieldList
): FilterList => [
  sectionHeader('systemProperties', 'SYSTEM PROPERTIES'),
  email(),
  primaryRole(roleList.mainRoles),
  customRoles(roleList.customRoles),
  groups(groupList),
  createSeparator(),
  lastActivityAt,
  lastLoginAt,
  joinDate,
  createSeparator(),
  ranks(rankList),
  badges(badgeList),
  // points(),
  createSeparator(),
  topics(),
  replies(),
  ...generateCustomProfileFieldFilters(customProfileFields)
]
